import dayjs from 'dayjs';

import type { IProperty } from 'src/services/api/properties/types';
import type { IShowing } from 'src/api/landlord-showings-api';

/** Tours are assumed to occupy a fixed 30-minute slot when checking overlap. */
const TOUR_DURATION_MS = 30 * 60 * 1000;

/**
 * Collect the IDs of every property in the building subtree rooted at
 * `buildingRootId` (the root itself plus all its descendant units).
 */
export function collectBuildingPropertyIds(properties: IProperty[], buildingRootId: number): Set<number> {
    const childrenByParent = new Map<number, number[]>();
    properties.forEach((p) => {
        if (p.parent_property_id != null) {
            childrenByParent.set(p.parent_property_id, [
                ...(childrenByParent.get(p.parent_property_id) ?? []),
                p.id,
            ]);
        }
    });
    const ids = new Set<number>();
    const stack = [buildingRootId];
    while (stack.length > 0) {
        const id = stack.pop() as number;
        if (ids.has(id)) {
            continue;
        }
        ids.add(id);
        (childrenByParent.get(id) ?? []).forEach((childId) => stack.push(childId));
    }
    return ids;
}

/**
 * Whether booking a tour at `slotStartMs` would exceed the building's
 * concurrent-tour cap. Counts non-canceled tours overlapping the slot within
 * the building subtree, excluding the tour being edited (`editingTourId`).
 */
export function exceedsTourConcurrency({
    maxConcurrentTours,
    slotStartMs,
    tours,
    buildingPropertyIds,
    editingTourId,
}: {
    maxConcurrentTours: number;
    slotStartMs: number;
    tours: IShowing[];
    buildingPropertyIds: Set<number>;
    editingTourId?: number;
}): boolean {
    const concurrent = tours.filter((tour) => {
        if (tour.is_canceled || tour.id === editingTourId) {
            return false;
        }
        if (!buildingPropertyIds.has(tour.property_id)) {
            return false;
        }
        const tourStart = dayjs(tour.start_time).valueOf();
        const tourEnd = tourStart + TOUR_DURATION_MS;
        return tourStart <= slotStartMs && slotStartMs < tourEnd;
    }).length;
    return concurrent >= maxConcurrentTours;
}
